import React, { useEffect, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist";
import { createTemplateFn } from "../../../../redux/reducers/TemplateField/templates/server";
import {
  createTemplate,
  getFragment,
  getTable,
  getTemplate,
  updateTemplate,
} from "src/docuclipper/api";
import {
  Rectangle,
  BACKEND_WIDTH,
  BACKEND_HEIGHT,
  BACKEND_PORTRAIT_WIDTH,
  BACKEND_PORTRAIT_HEIGHT,
  BACKEND_LANDSCAPE_WIDTH,
  BACKEND_LANDSCAPE_HEIGHT,
  getBackendDimensions,
  toBackendCoordinates,
  toDisplayCoordinates,
  toBackendCoordinatesImproved,
  toDisplayCoordinatesImproved,
  drawRectangles,
} from "./pdfHelpers";
import { useLocation, useHistory } from "react-router-dom";
import queryString from "query-string";
import useBankStatementPDF from "../../DataArchive/TransactionManager/useBankStatementPDF";
import { useDispatch, useSelector } from "react-redux";
import { createAlert } from "../../../../redux/reducers/Alerts";

import { RootState } from "../../../../redux/store";
import { TemplateFieldState } from "../../../../redux/reducers/TemplateFieldTypes";
import { ReduxState } from "src/redux";
import { Document } from "src/docuclipper/DocuclipperTypes";
import { useL1L2Params } from "src/hooks/useL1L2Params";
import ClipLoader from "react-spinners/ClipLoader";
import { ColumnConfig } from "./ColumnConfig";
import { Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { CsvToHtmlTable } from "../../../../react-csv-to-table";
import { parseCsvToRowsAndColumn } from "../../../../react-csv-to-table/utils";

// Set up PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.js`;

type Props = {};

type FieldType = "text" | "table";
type TextFieldType = "text" | "number" | "date";

type Box = {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  fieldType?: FieldType;
  textFieldType?: TextFieldType;
  columns?: number;
  columnPositions?: number[]; // Store as percentages (0-100)
  columnConfigs?: {
    name: string;
    type: TextFieldType;
    columnWidthPercentage?: number;
  }[];
  isDragging?: boolean;
  isResizing?: boolean;
  pageNumber?: number;
  appliesToAllPages?: boolean;
  name?: string;
  isConfiguring?: boolean; // Add this flag
};

type ConfigStep =
  | "fieldType"
  | "textFieldType"
  | "columns"
  | "columnConfig"
  | "pageConfig"
  | "complete";

const DrawRectangleV3_: React.FC<Props> = ({}) => {
  const location = useLocation();
  const query = queryString.parse(location.search);
  const documentId = query.documentId as string;
  const templateId = query.templateId
    ? parseInt(query.templateId as string, 10)
    : null;

  const history = useHistory();

  const {
    templateFields,
    page,
    selectedFieldId,
    canvasMode,
    template: templateObj,
    stage,
  } = useSelector(
    (state: ReduxState) => state.TemplateField as TemplateFieldState
  );

  const template = templateObj.template;

  const { files } = useSelector((state: ReduxState) => state.Files);
  let documentRow: Document | undefined;
  if (files.length > 0) {
    documentRow = files[0].document;
  }

  // Add state for template document ID
  const [templateDocumentId, setTemplateDocumentId] = useState<string | null>(
    null
  );

  // Update the PDF loading hook to use templateDocumentId when available
  const { pdfURL: imageSrc } = useBankStatementPDF({
    selectedDocumentId: templateId
      ? templateDocumentId
      : documentId || documentRow?.id,
  });

  // console.log({ templateDocumentId, documentId, documentRow, imageSrc });

  // Add minimal check for document availability

  // console.log({ imageSrc });

  // console.log("DrawRectangleV3_ component mounted with imageSrc:", imageSrc);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rectCanvasRef = useRef<HTMLCanvasElement>(null);
  const renderTaskRef = useRef<pdfjsLib.RenderTask | null>(null);
  const [pdfDoc, setPdfDoc] = useState<pdfjsLib.PDFDocumentProxy | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [scale, setScale] = useState<number>(1);
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPos, setStartPos] = useState<{ x: number; y: number } | null>(
    null
  );
  const [boxes, setBoxes] = useState<Box[]>([]);
  const [viewport, setViewport] = useState<pdfjsLib.PageViewport | null>(null);
  const drawingBoxRef = useRef<Box | null>(null);
  const pdfImageRef = useRef<ImageData | null>(null);
  const [selectedBoxId, setSelectedBoxId] = useState<string | null>(null);
  const [configStep, setConfigStep] = useState<ConfigStep>("complete");
  const [configuringBox, setConfiguringBox] = useState<Box | null>(null);
  const [columnCount, setColumnCount] = useState<number>(2);
  const [isDraggingColumn, setIsDraggingColumn] = useState<number | null>(null);
  const [isMoving, setIsMoving] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState<
    "nw" | "ne" | "sw" | "se" | "n" | "s" | "e" | "w" | null
  >(null);
  const [moveStartPos, setMoveStartPos] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [originalBoxPos, setOriginalBoxPos] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const [pageThumbnails, setPageThumbnails] = useState<
    Array<{ pageNumber: number; thumbnail: string }>
  >([]);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [popoverPosition, setPopoverPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const [extractedData, setExtractedData] = useState<string | null>(null);
  const [isTemplateNameValid, setIsTemplateNameValid] = useState(true);
  const [hasRendered, setHasRendered] = useState(false);

  const POPOVER_WIDTH = 300;
  const POPOVER_HEIGHT = 260; // Approximate, adjust as needed
  const POPOVER_GAP = 200;

  const dispatch = useDispatch();
  const { l1, l2 } = useL1L2Params();

  // Load PDF document and generate thumbnails
  useEffect(() => {
    let loadingTask: pdfjsLib.PDFDocumentLoadingTask | null = null;

    const loadPdf = async () => {
      try {
        // console.log("Starting PDF load from:", imageSrc);
        loadingTask = pdfjsLib.getDocument(imageSrc);
        // console.log("Loading task created");
        const pdf = await loadingTask.promise;
        // console.log("PDF loaded successfully:", pdf);
        setPdfDoc(pdf);
        setTotalPages(pdf.numPages);
        setIsPdfLoading(false);

        // Generate thumbnails for all pages
        const thumbnails: Array<{ pageNumber: number; thumbnail: string }> = [];
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const viewport = page.getViewport({ scale: 0.2, rotation: 0 }); // Small scale for thumbnails
          const canvas = document.createElement("canvas");
          const context = canvas.getContext("2d");
          canvas.width = viewport.width;
          canvas.height = viewport.height;

          await page.render({
            canvasContext: context!,
            viewport: viewport,
          }).promise;

          thumbnails.push({
            pageNumber: i,
            thumbnail: canvas.toDataURL(),
          });
        }
        setPageThumbnails(thumbnails);
      } catch (error) {
        if (error instanceof Error && error.message.includes("cancelled")) {
          // console.log("PDF loading was cancelled");
        } else {
          console.error("Error loading PDF:", error);
        }
        setIsPdfLoading(false);
      }
    };

    if (!imageSrc) {
      // console.log("No imageSrc provided, skipping PDF load");
      return;
    }

    setIsPdfLoading(true);
    loadPdf();

    // Cleanup function to cancel any ongoing loading task
    return () => {
      if (loadingTask) {
        // console.log("Cancelling PDF loading task");
        loadingTask.destroy();
      }
    };
  }, [imageSrc]);

  // Add effect to render page when PDF document is loaded
  useEffect(() => {
    if (pdfDoc && !isPdfLoading) {
      // console.log("PDF document loaded, triggering render");
      renderPage();
    }
  }, [pdfDoc, isPdfLoading]);

  // Add effect for template loading
  useEffect(() => {
    const loadTemplate = async () => {
      if (!templateId) return;

      try {
        const template = await getTemplate(templateId);
        const templateObj = JSON.parse(template.template);

        // Store the template's document ID
        setTemplateDocumentId(template.templateDocumentId?.toString() || null);

        // Convert template rectangles to our box format
        const newBoxes = templateObj.rectangles.map((rect: any) => {
          // Convert backend coordinates to display coordinates
          const displayCoords = toDisplayCoordinatesImproved(
            rect.fixedLocation.pageLocation.x0,
            rect.fixedLocation.pageLocation.y0,
            rect.fixedLocation.pageLocation.x1 -
              rect.fixedLocation.pageLocation.x0,
            rect.fixedLocation.pageLocation.y1 -
              rect.fixedLocation.pageLocation.y0,
            viewport,
            canvasRef.current?.width || 0,
            canvasRef.current?.height || 0
          );

          // For tables, ensure proper column setup
          const isTable = rect.toolType === "ocrtables";
          const columns = isTable ? rect.tableConfig?.columns || [] : [];
          const columnCount = isTable ? columns.length : 2;
          const columnPositions = isTable
            ? columns.map((col: any) => col.columnWidthPercentage) // Start from second column
            : [];
          const columnConfigs = isTable
            ? columns.map((col: any) => ({
                name: col.name || `Column ${col.index + 1}`,
                type: col.fieldType || "text",
                columnWidthPercentage: col.columnWidthPercentage,
              }))
            : [];
          // console.log({ columns, columnCount, columnPositions, columnConfigs });
          return {
            id: rect.id,
            x: displayCoords.x,
            y: displayCoords.y,
            width: displayCoords.width,
            height: displayCoords.height,
            fieldType: isTable ? "table" : "text",
            pageNumber: rect.pages.page + 1, // Convert from 0-based to 1-based
            appliesToAllPages: rect.pages.pagesType === "allPages",
            columns: columnCount,
            columnPositions,
            columnConfigs,
            name: rect.name,
          };
        });

        setBoxes(newBoxes);

        // Select the first box if available
        if (newBoxes.length > 0) {
          setSelectedBoxId(newBoxes[0].id);
        }
      } catch (error) {
        console.error("Error loading template:", error);
      }
    };

    loadTemplate();
  }, [templateId, viewport]); // Add viewport as dependency since we need it for coordinate conversion

  const renderPage = async () => {
    // Basic validation
    if (!pdfDoc || !canvasRef.current) {
      // console.log("Missing requirements:", { hasPdf: !!pdfDoc, hasCanvas: !!canvasRef.current });
      return;
    }

    // console.log("Starting renderPage for page:", currentPage);

    try {
      // 1. Cancel any ongoing render and wait for it to finish
      if (renderTaskRef.current) {
        // console.log("Cancelling previous render");
        try {
          await renderTaskRef.current.cancel();
        } catch (e) {
          // Ignore cancellation errors
        }
        renderTaskRef.current = null;
      }

      // 2. Get the page
      // console.log("Getting page:", currentPage);
      const page = await pdfDoc.getPage(currentPage);
      // console.log("Got page, rotate:", page.rotate);

      // 3. Get the viewport (preserve original aspect ratio)
      const viewport = page.getViewport({ scale });
      // console.log("Viewport dimensions:", { width: viewport.width, height: viewport.height, scale });

      // 4. Setup main canvas
      const canvas = canvasRef.current;
      const context = canvas.getContext("2d");
      if (!context) {
        // console.error("No canvas context");
        return;
      }

      // 5. Set canvas dimensions to match PDF (preserve aspect ratio)
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      canvas.style.width = `${viewport.width}px`;
      canvas.style.height = `${viewport.height}px`;

      // console.log("Canvas setup complete:", {
      //   canvasWidth: canvas.width,
      //   canvasHeight: canvas.height,
      //   styleWidth: canvas.style.width,
      //   styleHeight: canvas.style.height
      // });

      // 6. Setup rectangle canvas to match
      if (rectCanvasRef.current) {
        rectCanvasRef.current.width = viewport.width;
        rectCanvasRef.current.height = viewport.height;
        rectCanvasRef.current.style.width = `${viewport.width}px`;
        rectCanvasRef.current.style.height = `${viewport.height}px`;
      }

      // 7. Render PDF page
      // console.log("Starting PDF render");
      const renderTask = page.render({
        canvasContext: context,
        viewport: viewport,
      });

      // 8. Store reference and wait for completion
      renderTaskRef.current = renderTask;
      await renderTask.promise;
      // console.log("PDF render completed successfully");

      // 9. Update state and store image data
      setViewport(viewport);
      setHasRendered(true);
      pdfImageRef.current = context.getImageData(
        0,
        0,
        viewport.width,
        viewport.height
      );
      // console.log("Stored PDF image data, size:", pdfImageRef.current.data.length);

      // 10. Draw boxes if any exist
      // console.log("About to call drawBoxes, visible boxes count:", visibleBoxes.length);
      drawBoxes();
      // console.log("drawBoxes completed");
    } catch (error) {
      console.error("Render error:", error);
      if (error instanceof Error && !error.message.includes("cancelled")) {
        setHasRendered(false);
      }
    }
  };

  // Update effect to use actual PDF dimensions
  useEffect(() => {
    if (viewport && canvasRef.current && rectCanvasRef.current) {
      // Only update rectangle canvas dimensions, not the main canvas
      // The main canvas dimensions are handled in renderPage
      const pdfWidth = viewport.width;
      const pdfHeight = viewport.height;

      // Only update rect canvas since main canvas is handled in renderPage
      rectCanvasRef.current.width = pdfWidth;
      rectCanvasRef.current.height = pdfHeight;

      // Only redraw boxes, don't touch the main canvas
      drawBoxes();
    }
  }, [scale, viewport]);

  // Filter boxes to show only those for current page or all pages
  const visibleBoxes = boxes.filter(
    (box) =>
      (box.pageNumber === currentPage || box.appliesToAllPages) &&
      !box.isConfiguring // Add this condition to filter out configuring boxes
  );

  // Update drawBoxes to use the shared drawRectangles function
  const drawBoxes = () => {
    if (!rectCanvasRef.current) return;

    const context = rectCanvasRef.current.getContext("2d");
    if (!context) return;

    // Clear the canvas
    context.clearRect(
      0,
      0,
      rectCanvasRef.current.width,
      rectCanvasRef.current.height
    );

    // Convert boxes to rectangles format
    const rectangles: Rectangle[] = visibleBoxes.map((box) => ({
      id: box.id,
      x: box.x * scale,
      y: box.y * scale,
      width: box.width * scale,
      height: box.height * scale,
      fieldType: box.fieldType || "text",
      pageNumber: box.pageNumber || currentPage,
      columnPositions: box.columnPositions,
      name: box.name, // Add name field
    }));

    // If we're configuring a box, add it to the rectangles array
    if (configuringBox) {
      rectangles.push({
        id: configuringBox.id,
        x: configuringBox.x * scale,
        y: configuringBox.y * scale,
        width: configuringBox.width * scale,
        height: configuringBox.height * scale,
        fieldType: configuringBox.fieldType || "text",
        pageNumber: configuringBox.pageNumber || currentPage,
        columnPositions: configuringBox.columnPositions,
        name: configuringBox.name, // Add name field
      });
    }

    // Use the shared drawRectangles function
    drawRectangles(
      context,
      rectangles,
      selectedBoxId
        ? rectangles.find((r) => r.id === selectedBoxId) || null
        : null,
      scale
    );

    // Draw column dividers for tables
    rectangles.forEach((box) => {
      if (box.fieldType === "table" && box.columnPositions) {
        context.strokeStyle = "red";
        context.lineWidth = 2;
        context.setLineDash([]); // Solid lines for column dividers
        box.columnPositions.forEach((percentage) => {
          const pos = box.x + (box.width * percentage) / 100;

          // Draw the vertical line
          context.beginPath();
          context.moveTo(pos, box.y);
          context.lineTo(pos, box.y + box.height);
          context.stroke();

          // Draw triangles at the top and bottom
          const triangleSize = 16; // Increased from 8 to 16
          context.beginPath();
          // Top triangle
          context.moveTo(pos - triangleSize / 2, box.y);
          context.lineTo(pos + triangleSize / 2, box.y);
          context.lineTo(pos, box.y + triangleSize);
          context.closePath();
          context.fillStyle = "red";
          context.fill();

          // Bottom triangle
          context.beginPath();
          context.moveTo(pos - triangleSize / 2, box.y + box.height);
          context.lineTo(pos + triangleSize / 2, box.y + box.height);
          context.lineTo(pos, box.y + box.height - triangleSize);
          context.closePath();
          context.fill();
        });
      }
    });

    // Draw resize handles for selected box
    const selectedBox = visibleBoxes.find((box) => box.id === selectedBoxId);
    if (selectedBox) {
      const handleSize = 8;
      context.fillStyle = "#FF6B6B";
      context.setLineDash([]); // Solid lines for handles

      // Draw corner handles
      context.fillRect(
        selectedBox.x * scale - handleSize / 2,
        selectedBox.y * scale - handleSize / 2,
        handleSize,
        handleSize
      );
      context.fillRect(
        selectedBox.x * scale + selectedBox.width * scale - handleSize / 2,
        selectedBox.y * scale - handleSize / 2,
        handleSize,
        handleSize
      );
      context.fillRect(
        selectedBox.x * scale - handleSize / 2,
        selectedBox.y * scale + selectedBox.height * scale - handleSize / 2,
        handleSize,
        handleSize
      );
      context.fillRect(
        selectedBox.x * scale + selectedBox.width * scale - handleSize / 2,
        selectedBox.y * scale + selectedBox.height * scale - handleSize / 2,
        handleSize,
        handleSize
      );
    }

    // Draw the box being drawn
    if (drawingBoxRef.current) {
      context.strokeStyle = "red";
      context.lineWidth = 2;
      context.setLineDash([]); // Solid line for drawing
      context.strokeRect(
        drawingBoxRef.current.x * scale,
        drawingBoxRef.current.y * scale,
        drawingBoxRef.current.width * scale,
        drawingBoxRef.current.height * scale
      );
    }
  };

  // Render PDF page
  useEffect(() => {
    // console.log("Render effect triggered with:", {
    //   pdfDoc: !!pdfDoc,
    //   canvas: !!canvasRef.current,
    //   currentPage,
    //   scale,
    // });

    renderPage();

    // Cleanup function to cancel any ongoing render task
    return () => {
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }
    };
  }, [pdfDoc, currentPage, scale]);

  // Redraw boxes when boxes array changes
  useEffect(() => {
    if (!canvasRef.current || !viewport) return;

    const context = canvasRef.current.getContext("2d");
    if (!context) return;

    // Draw all boxes
    drawBoxes();
  }, [visibleBoxes]);

  // Add debugging function to print coordinate conversion
  const debugCoordinateConversion = (
    clientX: number,
    clientY: number,
    canvasElement: HTMLCanvasElement
  ) => {
    const rect = canvasElement.getBoundingClientRect();
    const scaleX = rect.width / canvasElement.width;
    const scaleY = rect.height / canvasElement.height;

    // Display coordinates (what we see on screen)
    const displayX = (clientX - rect.left) / scaleX;
    const displayY = (clientY - rect.top) / scaleY;

    // Backend coordinates using old method (hardcoded US Letter)
    const backendCoordsOld = toBackendCoordinates(
      displayX,
      displayY,
      0, // width not needed for click
      0, // height not needed for click
      viewport,
      canvasElement.width,
      canvasElement.height
    );

    // Backend coordinates using improved method (actual PDF dimensions)
    const backendCoordsImproved = toBackendCoordinatesImproved(
      displayX,
      displayY,
      0, // width not needed for click
      0, // height not needed for click
      viewport,
      canvasElement.width,
      canvasElement.height
    );

    // Get backend dimensions info for debugging
    const backendDims = getBackendDimensions(viewport);

    // Calculate click position as percentages of PDF dimensions
    const widthPercentage = (displayX / canvasElement.width) * 100;
    const heightPercentage = (displayY / canvasElement.height) * 100;

    // Calculate backend centering info for debugging
    const backendScaleX = backendDims.width / (viewport?.width || 1);
    const backendScaleY = backendDims.height / (viewport?.height || 1);
    const backendScale = Math.min(backendScaleX, backendScaleY);
    const scaledPdfWidth = (viewport?.width || 0) * backendScale;
    const scaledPdfHeight = (viewport?.height || 0) * backendScale;
    const offsetX = (backendDims.width - scaledPdfWidth) / 2;
    const offsetY = (backendDims.height - scaledPdfHeight) / 2;

    console.log("=== COORDINATE DEBUG ===");
    console.log(
      `🎯 CLICKED AT: ${widthPercentage.toFixed(
        1
      )}% WIDTH, ${heightPercentage.toFixed(1)}% HEIGHT`
    );
    console.log("━━━━━━━━━━━━━━━━━━━━━");
    console.log("📍 BACKEND CENTERING CALCULATION:");
    console.log(
      `   PDF size: ${viewport?.width || 0}×${viewport?.height || 0}`
    );
    console.log(
      `   Backend canvas: ${backendDims.width}×${backendDims.height}`
    );
    console.log(
      `   Scale factors: X=${backendScaleX.toFixed(
        3
      )}, Y=${backendScaleY.toFixed(3)}`
    );
    console.log(`   Backend scale (min): ${backendScale.toFixed(3)}`);
    console.log(
      `   Scaled PDF in backend: ${scaledPdfWidth.toFixed(
        1
      )}×${scaledPdfHeight.toFixed(1)}`
    );
    console.log(
      `   Centering offsets: X=${offsetX.toFixed(1)}, Y=${offsetY.toFixed(1)}`
    );
    console.log("━━━━━━━━━━━━━━━━━━━━━");
    console.log("📍 COORDINATE CONVERSION:");
    console.log(
      `   Browser click: (${displayX.toFixed(1)}, ${displayY.toFixed(1)})`
    );
    const pdfX = displayX * ((viewport?.width || 1) / canvasElement.width);
    const pdfY = displayY * ((viewport?.height || 1) / canvasElement.height);
    console.log(`   PDF coordinates: (${pdfX.toFixed(1)}, ${pdfY.toFixed(1)})`);
    const scaledX = pdfX * backendScale;
    const scaledY = pdfY * backendScale;
    console.log(`   Scaled: (${scaledX.toFixed(1)}, ${scaledY.toFixed(1)})`);
    console.log(
      `   With centering: (${(scaledX + offsetX).toFixed(1)}, ${(
        scaledY + offsetY
      ).toFixed(1)})`
    );
    console.log("━━━━━━━━━━━━━━━━━━━━━");
    console.log("📍 PERCENTAGE BREAKDOWN:");
    console.log(
      `   Width: ${widthPercentage.toFixed(
        1
      )}% (0% = left edge, 50% = center, 100% = right edge)`
    );
    console.log(
      `   Height: ${heightPercentage.toFixed(
        1
      )}% (0% = top edge, 50% = center, 100% = bottom edge)`
    );
    console.log("━━━━━━━━━━━━━━━━━━━━━");
    console.log("PDF orientation:", backendDims.orientation);
    console.log("BACKEND COORDINATES:");
    console.log(
      `   OLD (direct scale): (${backendCoordsOld.x}, ${backendCoordsOld.y})`
    );
    console.log(
      `   NEW (with centering): (${backendCoordsImproved.x}, ${backendCoordsImproved.y})`
    );
    console.log("========================");
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!canvasRef.current) return;

    // Debug coordinate conversion on every click
    debugCoordinateConversion(e.clientX, e.clientY, canvasRef.current);

    // console.log({ clientX: e.clientX, clientY: e.clientY });
    // If we're in configuration mode, check for column divider interaction
    if (configStep === "columns" && configuringBox) {
      const rect = canvasRef.current.getBoundingClientRect();
      const scaleX = rect.width / canvasRef.current.width;
      const scaleY = rect.height / canvasRef.current.height;
      const x = (e.clientX - rect.left) / scaleX;
      const y = (e.clientY - rect.top) / scaleY;

      // Check if clicking near a column divider
      const dividerWidth = 8; // Width of the clickable area for dividers
      const columnPositions = configuringBox.columnPositions || [];

      for (let i = 0; i < columnPositions.length; i++) {
        const dividerX =
          configuringBox.x + (configuringBox.width * columnPositions[i]) / 100;
        if (Math.abs(x - dividerX) <= dividerWidth / 2) {
          handleColumnDragStart(i);
          return;
        }
      }
      return;
    }

    // If we're in configuration mode, don't allow drawing new rectangles
    if (configStep !== "complete") {
      return;
    }

    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = rect.width / canvasRef.current.width;
    const scaleY = rect.height / canvasRef.current.height;
    const x = (e.clientX - rect.left) / scaleX;
    const y = (e.clientY - rect.top) / scaleY;

    // Check if clicking on an existing box
    const clickedBox = visibleBoxes.find((box) => {
      return (
        x >= box.x &&
        x <= box.x + box.width &&
        y >= box.y &&
        y <= box.y + box.height
      );
    });

    if (clickedBox) {
      // Only change selection if clicking on a different box
      if (clickedBox.id !== selectedBoxId) {
        // console.log("Selecting box:", clickedBox.id);
        setSelectedBoxId(clickedBox.id);
        drawBoxes();
      }

      const handleSize = 8; // Back to original size
      const borderSize = 8; // Size for edge detection
      const scaledBorderSize = borderSize / scale; // Adjust border size for scale

      // Check corners first
      if (
        Math.abs(x - clickedBox.x) <= handleSize / scale &&
        Math.abs(y - clickedBox.y) <= handleSize / scale
      ) {
        setResizeHandle("nw");
        setIsResizing(true);
        setOriginalBoxPos({
          x: clickedBox.x,
          y: clickedBox.y,
          width: clickedBox.width,
          height: clickedBox.height,
        });
        canvasRef.current.style.cursor = "nw-resize";
      } else if (
        Math.abs(x - (clickedBox.x + clickedBox.width)) <= handleSize / scale &&
        Math.abs(y - clickedBox.y) <= handleSize / scale
      ) {
        setResizeHandle("ne");
        setIsResizing(true);
        setOriginalBoxPos({
          x: clickedBox.x,
          y: clickedBox.y,
          width: clickedBox.width,
          height: clickedBox.height,
        });
        canvasRef.current.style.cursor = "ne-resize";
      } else if (
        Math.abs(x - clickedBox.x) <= handleSize / scale &&
        Math.abs(y - (clickedBox.y + clickedBox.height)) <= handleSize / scale
      ) {
        setResizeHandle("sw");
        setIsResizing(true);
        setOriginalBoxPos({
          x: clickedBox.x,
          y: clickedBox.y,
          width: clickedBox.width,
          height: clickedBox.height,
        });
        canvasRef.current.style.cursor = "sw-resize";
      } else if (
        Math.abs(x - (clickedBox.x + clickedBox.width)) <= handleSize / scale &&
        Math.abs(y - (clickedBox.y + clickedBox.height)) <= handleSize / scale
      ) {
        setResizeHandle("se");
        setIsResizing(true);
        setOriginalBoxPos({
          x: clickedBox.x,
          y: clickedBox.y,
          width: clickedBox.width,
          height: clickedBox.height,
        });
        canvasRef.current.style.cursor = "se-resize";
      }
      // Then check edges
      else if (
        Math.abs(x - clickedBox.x) <= scaledBorderSize &&
        y >= clickedBox.y &&
        y <= clickedBox.y + clickedBox.height
      ) {
        setResizeHandle("w");
        setIsResizing(true);
        setOriginalBoxPos({
          x: clickedBox.x,
          y: clickedBox.y,
          width: clickedBox.width,
          height: clickedBox.height,
        });
        canvasRef.current.style.cursor = "ew-resize";
      } else if (
        Math.abs(x - (clickedBox.x + clickedBox.width)) <= scaledBorderSize &&
        y >= clickedBox.y &&
        y <= clickedBox.y + clickedBox.height
      ) {
        setResizeHandle("e");
        setIsResizing(true);
        setOriginalBoxPos({
          x: clickedBox.x,
          y: clickedBox.y,
          width: clickedBox.width,
          height: clickedBox.height,
        });
        canvasRef.current.style.cursor = "ew-resize";
      } else if (
        Math.abs(y - clickedBox.y) <= scaledBorderSize &&
        x >= clickedBox.x &&
        x <= clickedBox.x + clickedBox.width
      ) {
        setResizeHandle("n");
        setIsResizing(true);
        setOriginalBoxPos({
          x: clickedBox.x,
          y: clickedBox.y,
          width: clickedBox.width,
          height: clickedBox.height,
        });
        canvasRef.current.style.cursor = "ns-resize";
      } else if (
        Math.abs(y - (clickedBox.y + clickedBox.height)) <= scaledBorderSize &&
        x >= clickedBox.x &&
        x <= clickedBox.x + clickedBox.width
      ) {
        setResizeHandle("s");
        setIsResizing(true);
        setOriginalBoxPos({
          x: clickedBox.x,
          y: clickedBox.y,
          width: clickedBox.width,
          height: clickedBox.height,
        });
        canvasRef.current.style.cursor = "ns-resize";
      }
      // Then check if inside the box (for moving)
      else if (
        x > clickedBox.x + borderSize &&
        x < clickedBox.x + clickedBox.width - borderSize &&
        y > clickedBox.y + borderSize &&
        y < clickedBox.y + clickedBox.height - borderSize
      ) {
        setIsMoving(true);
        setMoveStartPos({ x, y });
        setOriginalBoxPos({
          x: clickedBox.x,
          y: clickedBox.y,
          width: clickedBox.width,
          height: clickedBox.height,
        });
        canvasRef.current.style.cursor = "grab";
      }
      return;
    }

    // If not clicking on a box, start drawing a new one
    setIsDrawing(true);
    setStartPos({ x, y });
    drawingBoxRef.current = {
      id: Date.now().toString(),
      x,
      y,
      width: 0,
      height: 0,
    };
    canvasRef.current.style.cursor = "crosshair";
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!rectCanvasRef.current) return;

    const rect = rectCanvasRef.current.getBoundingClientRect();
    const scaleX = rect.width / rectCanvasRef.current.width;
    const scaleY = rect.height / rectCanvasRef.current.height;
    const x = (e.clientX - rect.left) / scaleX;
    const y = (e.clientY - rect.top) / scaleY;

    // Handle column dragging
    if (
      configStep === "columns" &&
      isDraggingColumn !== null &&
      configuringBox
    ) {
      handleColumnDrag(e);
      return;
    }

    // Update cursor based on position
    if (!isDrawing && !isMoving && !isResizing) {
      // Check if mouse is near a column divider
      if (configStep === "columns" && configuringBox) {
        const dividerWidth = 8; // Width of the clickable area for dividers
        const columnPositions = configuringBox.columnPositions || [];

        for (let i = 0; i < columnPositions.length; i++) {
          const dividerX =
            configuringBox.x +
            (configuringBox.width * columnPositions[i]) / 100;
          if (Math.abs(x - dividerX) <= dividerWidth / 2) {
            rectCanvasRef.current.style.cursor = "ew-resize";
            return;
          }
        }
      }
      rectCanvasRef.current.style.cursor = "default";
    }

    // Handle drawing new box
    if (isDrawing && startPos && drawingBoxRef.current) {
      drawingBoxRef.current.x = Math.min(startPos.x, x);
      drawingBoxRef.current.y = Math.min(startPos.y, y);
      drawingBoxRef.current.width = Math.abs(x - startPos.x);
      drawingBoxRef.current.height = Math.abs(y - startPos.y);
      drawBoxes();
    }
    // Handle moving existing box
    else if (isMoving && moveStartPos && originalBoxPos && selectedBoxId) {
      const dx = x - moveStartPos.x;
      const dy = y - moveStartPos.y;

      setBoxes((prev) =>
        prev.map((box) => {
          if (box.id === selectedBoxId) {
            return {
              ...box,
              x: originalBoxPos.x + dx,
              y: originalBoxPos.y + dy,
            };
          }
          return box;
        })
      );
      if (canvasRef.current) {
        canvasRef.current.style.cursor = "grabbing";
      }
      drawBoxes();
    }
    // Handle resizing existing box
    else if (isResizing && originalBoxPos && selectedBoxId) {
      setBoxes((prev) =>
        prev.map((box) => {
          if (box.id === selectedBoxId) {
            let newBox = { ...box };
            switch (resizeHandle) {
              case "nw":
                newBox.x = x;
                newBox.y = y;
                newBox.width = originalBoxPos.x + originalBoxPos.width - x;
                newBox.height = originalBoxPos.y + originalBoxPos.height - y;
                break;
              case "ne":
                newBox.y = y;
                newBox.width = x - originalBoxPos.x;
                newBox.height = originalBoxPos.y + originalBoxPos.height - y;
                break;
              case "sw":
                newBox.x = x;
                newBox.width = originalBoxPos.x + originalBoxPos.width - x;
                newBox.height = y - originalBoxPos.y;
                break;
              case "se":
                newBox.width = x - originalBoxPos.x;
                newBox.height = y - originalBoxPos.y;
                break;
              case "e":
                newBox.width = x - originalBoxPos.x;
                break;
              case "w":
                newBox.x = x;
                newBox.width = originalBoxPos.x + originalBoxPos.width - x;
                break;
              case "n":
                newBox.y = y;
                newBox.height = originalBoxPos.y + originalBoxPos.height - y;
                break;
              case "s":
                newBox.height = y - originalBoxPos.y;
                break;
            }
            return newBox;
          }
          return box;
        })
      );
      drawBoxes();
    }
  };

  const handleBoxSelect = (boxId: string) => {
    // console.log("Selecting box from dropdown:", boxId);
    // Only update if selecting a different box
    if (boxId !== selectedBoxId) {
      setSelectedBoxId(boxId);
      // Force redraw when selection changes
      if (canvasRef.current) {
        const context = canvasRef.current.getContext("2d");
        if (context) {
          drawBoxes();
        }
      }
    }
  };

  // Add effect to redraw when selection changes
  useEffect(() => {
    drawBoxes();
  }, [selectedBoxId]);

  const selectedBox = visibleBoxes.find((box) => box.id === selectedBoxId);

  const startConfiguration = (box: Box) => {
    setConfiguringBox(box);
    setConfigStep("fieldType");

    // Get the canvas and its rendered size
    const canvas = canvasRef.current;
    const canvasRect = canvas?.getBoundingClientRect();
    if (!canvas || !canvasRect) {
      setPopoverPosition({
        x: box.x,
        y: box.y + box.height + POPOVER_GAP,
      });
      return;
    }

    // Calculate scale between canvas internal size and rendered size
    const scaleX = canvasRect.width / canvas.width;
    const scaleY = canvasRect.height / canvas.height;

    // Rectangle position in rendered (screen) coordinates
    const rectLeft = box.x * scaleX;
    const rectTop = box.y * scaleY;
    const rectWidth = box.width * scaleX;

    // Always position popover to the right of the rectangle
    const left = rectLeft + rectWidth + POPOVER_GAP;
    const top = rectTop;

    setPopoverPosition({ x: left, y: top });
  };

  const handleFieldTypeSelect = (type: FieldType) => {
    if (!configuringBox) return;

    // Create a new box with the same coordinates but new field type
    const updatedBox = {
      ...configuringBox,
      fieldType: type,
    };
    setConfiguringBox(updatedBox);

    if (type === "text") {
      // For text fields, move to text field type configuration
      setConfigStep("textFieldType");
    } else {
      // For tables, move to column configuration
      setConfigStep("columns");
      // Initialize column positions evenly
      const positions = [
        0,
        ...Array.from(
          { length: columnCount - 1 },
          (_, i) => ((i + 1) * 100) / columnCount
        ),
      ];
      // console.log("Initializing column positions:", positions);
      setConfiguringBox({
        ...updatedBox,
        columnPositions: positions,
        columns: columnCount,
        columnConfigs: Array(columnCount)
          .fill(null)
          .map((_, i) => ({
            name: `Column ${i + 1}`,
            type: "text",
          })),
      });
    }
  };

  const handleTextFieldTypeSelect = (type: TextFieldType) => {
    if (!configuringBox) return;

    const updatedBox = {
      ...configuringBox,
      textFieldType: type,
    };
    setConfiguringBox(updatedBox);
    setBoxes((prev) =>
      prev.map((box) => (box.id === updatedBox.id ? updatedBox : box))
    );
    setConfigStep("pageConfig");
  };

  const handleColumnConfigChange = (
    index: number,
    field: "name" | "type",
    value: string
  ) => {
    if (!configuringBox) return;

    const newColumnConfigs = [...(configuringBox.columnConfigs || [])];
    newColumnConfigs[index] = {
      ...newColumnConfigs[index],
      [field]: value,
    };

    setConfiguringBox({
      ...configuringBox,
      columnConfigs: newColumnConfigs,
    });
  };

  const handleApplyToAllPagesChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!configuringBox) return;

    const checked = e.target?.checked ?? false;
    setConfiguringBox({
      ...configuringBox,
      appliesToAllPages: checked,
      pageNumber: checked ? undefined : currentPage,
    });
  };

  const handleSelectedBoxApplyToAllPagesChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!selectedBoxId) return;

    const checked = e.target?.checked ?? false;
    setBoxes((prev) =>
      prev.map((box) =>
        box.id === selectedBoxId
          ? {
              ...box,
              appliesToAllPages: checked,
              pageNumber: checked ? undefined : currentPage,
            }
          : box
      )
    );
  };

  const handleColumnCountChange = (count: number) => {
    // Validate count is between 2 and 10
    const validCount = Math.max(2, Math.min(10, Math.floor(count) || 2));
    setColumnCount(validCount);
    if (configuringBox) {
      // Calculate even spacing for new column count using percentages
      const positions = Array.from(
        { length: validCount - 1 },
        (_, i) => ((i + 1) * 100) / validCount
      );
      // console.log("Updating column positions:", positions);

      setConfiguringBox({
        ...configuringBox,
        columns: validCount,
        columnPositions: positions,
        columnConfigs: Array(validCount)
          .fill(null)
          .map((_, i) => ({
            name: `Column ${i + 1}`,
            type: "text",
            columnWidthPercentage: i === 0 ? 0 : positions[i - 1],
          })),
      });
    }
  };

  const handleColumnDragStart = (index: number) => {
    setIsDraggingColumn(index);
    if (canvasRef.current) {
      canvasRef.current.style.cursor = "ew-resize";
    }
  };

  const handleColumnDrag = (e: React.MouseEvent) => {
    if (isDraggingColumn === null || !configuringBox || !canvasRef.current)
      return;

    // Ensure cursor stays as ew-resize during drag
    canvasRef.current.style.cursor = "ew-resize";

    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = rect.width / canvasRef.current.width;
    const scaleY = rect.height / canvasRef.current.height;

    // Get mouse position in canvas coordinates
    const x = (e.clientX - rect.left) / scaleX;

    // Calculate position relative to the rectangle
    const relativeX = x - configuringBox.x;

    // Calculate percentage within the rectangle
    const percentage = (relativeX / configuringBox.width) * 100;

    // Get current positions
    const positions = [...(configuringBox.columnPositions || [])];

    // Calculate min and max bounds for this column
    const minPercentage = 0;
    const maxPercentage = 100;

    // Get the positions of adjacent columns
    const prevColumnPos =
      isDraggingColumn > 0 ? positions[isDraggingColumn - 1] : minPercentage;
    const nextColumnPos =
      isDraggingColumn < positions.length - 1
        ? positions[isDraggingColumn + 1]
        : maxPercentage;

    // Ensure the position stays within bounds and between adjacent columns
    const boundedPercentage = Math.max(
      prevColumnPos + 2, // Minimum 2% gap from previous column
      Math.min(nextColumnPos - 2, percentage) // Minimum 2% gap from next column
    );

    // Update the positions array
    positions[isDraggingColumn] = boundedPercentage;

    // Update the configuring box with new positions and column widths
    const updatedBox = {
      ...configuringBox,
      columnPositions: positions,
      columnConfigs: configuringBox.columnConfigs?.map((config, index) => {
        // For each column, set its columnWidthPercentage to its left position
        const leftPosition = index === 0 ? 0 : positions[index - 1];
        return {
          ...config,
          columnWidthPercentage: leftPosition,
        };
      }),
    };
    setConfiguringBox(updatedBox);

    // Update the boxes array to persist the changes
    setBoxes((prevBoxes) =>
      prevBoxes.map((box) => (box.id === configuringBox.id ? updatedBox : box))
    );
  };

  const handleColumnDragEnd = () => {
    setIsDraggingColumn(null);
    if (canvasRef.current) {
      canvasRef.current.style.cursor = "default";
    }
    // Restore the popover position after dragging ends
    if (configuringBox) {
      const canvas = canvasRef.current;
      const canvasRect = canvas?.getBoundingClientRect();
      if (canvas && canvasRect) {
        const scaleX = canvasRect.width / canvas.width;
        const scaleY = canvasRect.height / canvas.height;
        const rectLeft = configuringBox.x * scaleX;
        const rectTop = configuringBox.y * scaleY;

        // Position popover to the right of the page
        const left = canvasRect.width - POPOVER_WIDTH - POPOVER_GAP;
        const top = rectTop - POPOVER_HEIGHT - POPOVER_GAP;

        setPopoverPosition({ x: left, y: top });
      }

      // Trigger data extraction with updated column positions
      fetchRectangleData(configuringBox);
    }
  };

  const completeConfiguration = (boxToComplete?: Box) => {
    const sourceBox = boxToComplete || configuringBox;
    if (!sourceBox) return;
    const finalBox = {
      ...sourceBox,
      columns: columnCount,
      columnPositions: sourceBox.columnPositions,
      columnConfigs: sourceBox.columnConfigs?.map((config, index) => ({
        ...config,
        columnWidthPercentage: config.columnWidthPercentage, // Preserve the existing width
      })),
      isConfiguring: false, // Mark as configured
    };
    // console.log("Completing configuration with box:", finalBox);
    setBoxes((prev) =>
      prev.map((box) => (box.id === finalBox.id ? finalBox : box))
    );
    setConfigStep("complete");
    setConfiguringBox(null);
    fetchRectangleData(finalBox); // Pass the configured box
  };

  const handleMouseUp = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!canvasRef.current) return;

    // Stop column dragging if active
    if (configStep === "columns" && isDraggingColumn !== null) {
      setIsDraggingColumn(null);
      // Restore popover position
      if (configuringBox) {
        const canvas = canvasRef.current;
        const canvasRect = canvas?.getBoundingClientRect();
        if (canvas && canvasRect) {
          const scaleX = canvasRect.width / canvas.width;
          const scaleY = canvasRect.height / canvas.height;
          const rectLeft = configuringBox.x * scaleX;
          const rectTop = configuringBox.y * scaleY;
          const rectWidth = configuringBox.width * scaleX;

          // Always position popover to the right of the rectangle
          const left = rectLeft + rectWidth + POPOVER_GAP;
          const top = rectTop;

          setPopoverPosition({ x: left, y: top });
        }
      }
      return;
    }

    if (isDrawing && startPos && drawingBoxRef.current) {
      const rect = canvasRef.current.getBoundingClientRect();
      const scaleX = rect.width / canvasRef.current.width;
      const scaleY = rect.height / canvasRef.current.height;
      const x = (e.clientX - rect.left) / scaleX;
      const y = (e.clientY - rect.top) / scaleY;

      if (x !== startPos.x && y !== startPos.y) {
        // Get the next field number
        const nextFieldNumber = boxes.length + 1;
        const newBox = {
          id: drawingBoxRef.current.id,
          x: Math.min(startPos.x, x),
          y: Math.min(startPos.y, y),
          width: Math.abs(x - startPos.x),
          height: Math.abs(y - startPos.y),
          pageNumber: currentPage,
          appliesToAllPages: false,
          name: `Field ${nextFieldNumber}`, // Add sequential name
          isConfiguring: true, // Mark as configuring
        };

        setBoxes((prev) => [...prev, newBox]);
        // Select the newly created box
        setSelectedBoxId(newBox.id);
        startConfiguration(newBox);
      }
    }

    setIsDrawing(false);
    setIsMoving(false);
    setIsResizing(false);
    setResizeHandle(null);
    setMoveStartPos(null);
    setOriginalBoxPos(null);
    setStartPos(null);
    drawingBoxRef.current = null;
    canvasRef.current.style.cursor = "crosshair";

    if (!isDrawing) {
      const selectedBox = boxes.find((box) => box.id === selectedBoxId);
      fetchRectangleData(selectedBox); // when moving rectangle around
    }
  };

  const handleDeleteBox = () => {
    if (selectedBoxId) {
      setBoxes((prev) => {
        const newBoxes = prev.filter((box) => box.id !== selectedBoxId);
        // Select the last box if there are any boxes left
        if (newBoxes.length > 0) {
          setSelectedBoxId(newBoxes[newBoxes.length - 1].id);
        } else {
          setSelectedBoxId(null);
        }
        return newBoxes;
      });
    }
  };

  const handleSaveTemplate = async (createJob: boolean = false) => {
    // console.log({
    //   boxes,
    // });
    const query = queryString.parse(location.search);
    const templateNameFromQuery = query.templateName as string;
    if (!templateId && !templateNameFromQuery && !template?.name?.trim()) {
      dispatch(
        createAlert({
          type: "error",
          message: "Template name is required",
          timeout: 5000,
        })
      );
      return;
    }

    // Convert boxes to template fields format with backend coordinates
    const templateFields = boxes.map((box) => {
      const backendCoords = toBackendCoordinatesImproved(
        box.x,
        box.y,
        box.width,
        box.height,
        viewport,
        canvasRef.current?.width || 0,
        canvasRef.current?.height || 0
      );

      // console.log("Template coordinates:", {
      //   original: {
      //     x: box.x,
      //     y: box.y,
      //     width: box.width,
      //     height: box.height,
      //   },
      //   viewport: viewport
      //     ? { width: viewport.width, height: viewport.height }
      //     : null,
      //   backendCoords,
      //   toBackendCoordinates: toBackendCoordinates.toString(),
      // });

      return {
        id: box.id,
        name: box.name || `Field ${box.id.slice(-4)}`,
        fieldType: box.fieldType === "table" ? "text" : "text", // Default to text if not specified
        toolType: box.fieldType === "table" ? "ocrtables" : "ocr",
        locationType: "fixed",
        fixedLocation: {
          pageLocation: {
            x0: backendCoords.x,
            y0: backendCoords.y,
            x1: backendCoords.x + backendCoords.width,
            y1: backendCoords.y + backendCoords.height,
          },
        },
        variableLocation: {
          variableLocations: [],
        },
        pages: {
          page: (box.pageNumber || 1) - 1 || 0,
          pagesType: box.appliesToAllPages ? "allPages" : "currentPage",
          include: [],
          exclude: [],
        },
        tableConfig:
          box.fieldType === "table"
            ? {
                columns: Array.from(
                  { length: box.columns || 0 },
                  (_, index) => {
                    // For first column, use 0 as position
                    // For other columns, use the position from columnPositions array (which is 1 less than columns)
                    const pos =
                      index === 0
                        ? 0
                        : (box.columnPositions || [])[index - 1] || 0;
                    return {
                      name:
                        box.columnConfigs?.[index]?.name ||
                        `Column ${index + 1}`,
                      index: index,
                      fieldType: box.columnConfigs?.[index]?.type || "text",
                      columnWidthPercentage: pos,
                      removeFromOutput: false,
                    };
                  }
                ),
                headerRows: 0,
                transforms: {
                  verticalMergeMasks: null,
                  removeTopRows: null,
                  removeBottomRows: null,
                },
              }
            : {
                columns: [],
                headerRows: 0,
                transforms: {
                  verticalMergeMasks: null,
                  removeTopRows: null,
                  removeBottomRows: null,
                },
              },
        checkLabels: {
          checked: "yes",
          unchecked: "no",
        },
      };
    });

    try {
      // console.log("All boxes before conversion:", boxes);
      // console.log("Template fields being sent:", templateFields);

      const templateData = {
        ...(templateId
          ? {}
          : { name: templateNameFromQuery || template?.name || "" }), // Use templateName from query if available
        metadataFields: [],
        logoURL: "",
        intuitNumber: "",
        visibility: "private",
        category: "",
        type: "FreeForm",
        description: "",
        version: "v2",
        template: JSON.stringify({
          rectangles: templateFields,
        }),
        templateDocumentId: documentRow ? documentRow.id : documentId,
        createJob,
        l1,
        l2,
      };

      const newTemplate = templateId
        ? await updateTemplate(templateId, templateData)
        : await createTemplate(templateData);

      // console.log("Template saved successfully:", newTemplate);

      // Show success message based on action taken
      const baseMessage = templateId ? "Template updated" : "Template created";
      const fullMessage = createJob
        ? `${baseMessage} and data extraction started`
        : `${baseMessage} successfully`;

      dispatch(
        createAlert({
          type: "success",
          message: fullMessage,
          timeout: 5000,
        })
      );

      // Only navigate to job data if createJob was true and a job was created
      if (createJob && newTemplate.job && newTemplate.job.id) {
        // Navigate to jobData page
        const queryParams = new URLSearchParams(history.location.search);
        queryParams.append("jobId", newTemplate.job.id);
        queryParams.append("jobType", "template");
        queryParams.append(
          "newDocuments",
          documentRow ? documentRow.id.toString() : documentId
        );
        history.push(`/jobData?${queryParams.toString()}`);
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      if (errorMessage.includes("Validation error")) {
        setIsTemplateNameValid(false);
      }
      dispatch(
        createAlert({
          type: "error",
          message: errorMessage.includes("Validation error")
            ? "Template name already exists"
            : "Error saving template",
          timeout: 5000,
        })
      );
    }
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!selectedBoxId || !e.target) return;
    const newName = e.target.value;
    setBoxes((prev) =>
      prev.map((box) =>
        box.id === selectedBoxId ? { ...box, name: newName } : box
      )
    );
  };

  const fetchRectangleData = async (box?: Box) => {
    if (!box || !documentId) {
      // console.log("missing", box, documentId);
      return;
    }

    try {
      setIsLoadingData(true);
      setExtractedData(null);

      const { x, y, width, height } = box;
      const x0 = x;
      const x1 = x + width;
      const y0 = y;
      const y1 = y + height;

      let data;
      if (box.fieldType === "text") {
        const x0 = box.x;
        const y0 = box.y;
        const x1 = box.x + box.width;
        const y1 = box.y + box.height;

        if (!viewport || !canvasRef.current) {
          console.error("Viewport or canvas not available");
          return;
        }

        const backendCoords = toBackendCoordinatesImproved(
          x0,
          y0,
          box.width,
          box.height,
          viewport,
          canvasRef.current?.width || 0,
          canvasRef.current?.height || 0
        );

        data = await getFragment({
          checkLabels: { checked: "true", unchecked: "false" },
          fieldType: "text",
          fragmentId: null,
          jobId: null,
          documentId: documentId as any,
          pageNumber: (box.pageNumber || currentPage) - 1,
          toolType: "ocr",
          x0: backendCoords.x,
          x1: backendCoords.x + backendCoords.width,
          y0: backendCoords.y,
          y1: backendCoords.y + backendCoords.height,
        });
      } else if (box.fieldType === "table") {
        const x0 = box.x;
        const y0 = box.y;
        const x1 = box.x + box.width;
        const y1 = box.y + box.height;

        if (!viewport || !canvasRef.current) {
          console.error("Viewport or canvas not available");
          return;
        }

        const backendCoords = toBackendCoordinatesImproved(
          x0,
          y0,
          box.width,
          box.height,
          viewport,
          canvasRef.current?.width || 0,
          canvasRef.current?.height || 0
        );

        data = await getTable({
          fragmentId: null,
          jobId: null,
          documentId: documentId as any,
          pageNumber: (box.pageNumber || currentPage) - 1,
          toolType: "tables",
          x0: backendCoords.x,
          x1: backendCoords.x + backendCoords.width,
          y0: backendCoords.y,
          y1: backendCoords.y + backendCoords.height,
          tableConfig: {
            columns:
              box.columnConfigs?.map((col, index) => ({
                name: col.name,
                type: col.type,
                index,
                fieldType: "text",
                columnWidthPercentage: col.columnWidthPercentage as any,
                line: {
                  id: `line-${index}`,
                  horizontalLine: null,
                  stroke: "#000",
                  strokeWidth: 1,
                  verticalLine: null,
                  type: "vertical",
                  draggable: false,
                  text: "",
                  dash: null,
                },
              })) || [],
            headerRows: 1,
            transforms: {
              verticalMergeMasksEnabled: false,
              alignMiddleEnabled: false,
              verticalMergeMasks: null,
              alignMiddle: null,
            },
          },
        });
      }

      setExtractedData(JSON.stringify(data, null, 2));
    } catch (error) {
      console.error("Error fetching rectangle data:", error);
      setExtractedData("Error fetching data. Please try again.");
    } finally {
      setIsLoadingData(false);
    }
  };

  // Update the condition for showing the "Select data to extract" message
  const isPageRendered = hasRendered && pdfDoc !== null;

  // Add keyboard event handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" || e.key === "Delete") {
        if (configuringBox) {
          // Cancel configuration
          setConfiguringBox(null);
          setConfigStep("complete");
        } else if (selectedBoxId) {
          // Delete selected box
          handleDeleteBox();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [configuringBox, selectedBoxId]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "20px",
        padding: "20px",
        position: "relative",
        height: "85vh",
        width: "100%",
      }}
    >
      {/* Controls Bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          padding: "10px",
          background: "#f5f5f5",
          borderRadius: "8px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        }}
      >
        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          {/* <button
            onClick={() => setScale(scale + 0.1)}

            style={{
              padding: "6px 12px",
              background: "#fff",
              border: "1px solid #ddd",
              borderRadius: "4px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "4px",
              fontSize: "13px",
            }}
          >
            <span>🔍+</span> Zoom In
          </button>
          <button
            onClick={() => setScale(Math.max(0.1, scale - 0.1))}
            style={{
              padding: "6px 12px",
              background: "#fff",
              border: "1px solid #ddd",
              borderRadius: "4px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "4px",
              fontSize: "13px",
            }}
          >
            <span>🔍-</span> Zoom Out
          </button> */}
        </div>

        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            style={{
              padding: "6px 12px",
              background: "#fff",
              border: "1px solid #ddd",
              borderRadius: "4px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "4px",
              fontSize: "13px",
            }}
          >
            <span>←</span> Previous
          </button>
          <span style={{ padding: "0 10px", fontSize: "13px" }}>
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() =>
              setCurrentPage(Math.min(totalPages, currentPage + 1))
            }
            style={{
              padding: "6px 12px",
              background: "#fff",
              border: "1px solid #ddd",
              borderRadius: "4px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "4px",
              fontSize: "13px",
            }}
          >
            Next <span>→</span>
          </button>
        </div>
      </div>

      <div style={{ display: "flex", gap: "20px", flex: 1, minHeight: 0 }}>
        {/* Page thumbnails sidebar */}
        <div
          style={{
            width: "120px",
            padding: "10px",
            background: "#f5f5f5",
            borderRadius: "8px",
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            overflowY: "auto",
            maxHeight: "100%",
            flexShrink: 0,
          }}
        >
          {pageThumbnails.map(({ pageNumber, thumbnail }) => (
            <div
              key={pageNumber}
              style={{
                cursor: "pointer",
                border:
                  pageNumber === currentPage
                    ? "2px solid #FF6B6B"
                    : "1px solid #ccc",
                borderRadius: "4px",
                padding: "4px",
                background: "white",
              }}
              onClick={() => setCurrentPage(pageNumber)}
            >
              <img
                src={thumbnail}
                alt={`Page ${pageNumber}`}
                style={{
                  width: "100%",
                  height: "auto",
                  display: "block",
                }}
              />
              <div
                style={{
                  textAlign: "center",
                  fontSize: "12px",
                  marginTop: "4px",
                }}
              >
                Page {pageNumber}
              </div>
            </div>
          ))}
        </div>

        <div
          style={{
            flex: "1 1 auto",
            position: "relative",
            minWidth: 0,
            height: "100%",
          }}
        >
          <div style={{ position: "relative", height: "100%" }}>
            <canvas
              ref={canvasRef}
              style={{
                display: "block",
                margin: "0 auto",
                border: "2px solid #000000", // Thick red border for debugging
                background: "#fafafa",
                position: "absolute",
                top: 0,
                left: 0,
                zIndex: 1,
              }}
            />
            <canvas
              ref={rectCanvasRef}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              style={{
                display: "block",
                margin: "0 auto",
                position: "absolute",
                top: 0,
                left: 0,
                cursor: "crosshair",
                zIndex: 2,
              }}
            />
            {boxes.length === 0 && !isLoadingData && isPageRendered && (
              <div
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  background: "rgba(255, 255, 255, 0.95)",
                  padding: "20px 30px",
                  borderRadius: "8px",
                  boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
                  textAlign: "center",
                  pointerEvents: "none",
                  zIndex: 1000,
                  border: "2px solid red",
                }}
              >
                <h3 style={{ margin: "0 0 10px 0", fontSize: "18px" }}>
                  Select data to extract 🔍
                </h3>
                <p style={{ margin: 0, fontSize: "14px", color: "#666" }}>
                  Click and drag to select the data you want to extract
                </p>
                {/* <div
                  style={{
                    marginTop: "20px",
                    width: "100px",
                    height: "60px",
                    border: "2px solid #4CAF50",
                    borderRadius: "4px",
                    animation: "pulse 2s infinite",
                  }}
                />*/}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Configuration Popover */}
      {configStep !== "complete" && configuringBox && popoverPosition && (
        <div
          style={{
            position: "absolute",
            left: popoverPosition.x,
            top: popoverPosition.y,
            width: "300px",
            padding: "20px",
            background: "white",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            zIndex: 1001,
            pointerEvents: "auto", // Ensure the popover is interactive
          }}
        >
          {/* <h3 style={{ margin: "0 0 15px 0" }}>Configure Rectangle</h3> */}
          <div
            style={{
              marginBottom: "15px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <div
              style={{
                padding: "4px 8px",
                background: configStep === "fieldType" ? "#4CAF50" : "#e0e0e0",
                color: configStep === "fieldType" ? "white" : "black",
                borderRadius: "12px",
                fontSize: "12px",
              }}
            >
              1
            </div>
            <div
              style={{
                padding: "4px 8px",
                background:
                  configStep === "textFieldType" ||
                  configStep === "columns" ||
                  configStep === "columnConfig"
                    ? "#4CAF50"
                    : "#e0e0e0",
                color:
                  configStep === "textFieldType" ||
                  configStep === "columns" ||
                  configStep === "columnConfig"
                    ? "white"
                    : "black",
                borderRadius: "12px",
                fontSize: "12px",
              }}
            >
              2
            </div>
            <div
              style={{
                padding: "4px 8px",
                background: configStep === "pageConfig" ? "#4CAF50" : "#e0e0e0",
                color: configStep === "pageConfig" ? "white" : "black",
                borderRadius: "12px",
                fontSize: "12px",
              }}
            >
              3
            </div>
          </div>

          {configStep === "pageConfig" && (
            <div>
              <h4 style={{ margin: "0 0 10px 0" }}>Page Configuration</h4>
              <div style={{ marginBottom: "20px" }}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                  }}
                >
                  <button
                    onClick={() => {
                      const updatedBox = {
                        ...configuringBox,
                        appliesToAllPages: false,
                        pageNumber: currentPage,
                      };
                      setConfiguringBox(updatedBox);
                      setBoxes((prev) =>
                        prev.map((box) =>
                          box.id === selectedBoxId ? updatedBox : box
                        )
                      );
                      completeConfiguration(updatedBox);
                    }}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px",
                      padding: "12px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      cursor: "pointer",
                      background: "white",
                      textAlign: "left",
                    }}
                  >
                    <div style={{ fontWeight: "500" }}>Current Page Only</div>
                    <div
                      style={{
                        fontSize: "13px",
                        color: "#666",
                      }}
                    >
                      This field will only appear on page {currentPage}. Use
                      this for fields that are unique to this page.
                    </div>
                  </button>

                  <button
                    onClick={() => {
                      const updatedBox = {
                        ...configuringBox,
                        appliesToAllPages: true,
                        pageNumber: undefined,
                      };
                      setConfiguringBox(updatedBox);
                      setBoxes((prev) =>
                        prev.map((box) =>
                          box.id === selectedBoxId ? updatedBox : box
                        )
                      );
                      completeConfiguration(updatedBox);
                    }}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px",
                      padding: "12px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      cursor: "pointer",
                      background: "white",
                      textAlign: "left",
                    }}
                  >
                    <div style={{ fontWeight: "500" }}>All Pages</div>
                    <div
                      style={{
                        fontSize: "13px",
                        color: "#666",
                      }}
                    >
                      This field will appear on every page of the document. Use
                      this for fields that repeat across pages.
                    </div>
                  </button>
                </div>
              </div>
            </div>
          )}

          {configStep === "fieldType" && (
            <div>
              <h4 style={{ margin: "0 0 10px 0" }}>Select Field Type</h4>
              <div
                style={{ display: "flex", gap: "10px", marginBottom: "20px" }}
              >
                <button
                  onClick={() => handleFieldTypeSelect("text")}
                  style={{
                    padding: "10px",
                    flex: 1,
                    background: "#fff",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                >
                  Text Field
                </button>
                <button
                  onClick={() => handleFieldTypeSelect("table")}
                  style={{
                    padding: "10px",
                    flex: 1,
                    background: "#fff",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                >
                  Table
                </button>
              </div>
            </div>
          )}

          {configStep === "textFieldType" && (
            <div>
              <h4 style={{ margin: "0 0 10px 0" }}>Select Text Field Type</h4>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                  marginBottom: "20px",
                }}
              >
                <button
                  onClick={() => handleTextFieldTypeSelect("text")}
                  style={{
                    padding: "10px",
                    background: "#fff",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                >
                  Text
                </button>
                <button
                  onClick={() => handleTextFieldTypeSelect("number")}
                  style={{
                    padding: "10px",
                    background: "#fff",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                >
                  Number
                </button>
                <button
                  onClick={() => handleTextFieldTypeSelect("date")}
                  style={{
                    padding: "10px",
                    background: "#fff",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                >
                  Date
                </button>
              </div>
            </div>
          )}

          {configStep === "columns" && (
            <div>
              <h4 style={{ margin: "0 0 10px 0" }}>Configure Table Columns</h4>
              <div style={{ marginBottom: "20px" }}>
                <div style={{ marginBottom: "10px" }}>
                  <label>Number of Columns:</label>
                  <input
                    type="number"
                    min="2"
                    max="10"
                    value={columnCount}
                    onChange={(e) =>
                      handleColumnCountChange(parseInt(e.target.value))
                    }
                    style={{
                      width: "100%",
                      padding: "8px",
                      marginTop: "5px",
                      borderRadius: "4px",
                      border: "1px solid #ccc",
                    }}
                  />
                </div>
                <div
                  style={{
                    border: "2px dashed #FF6B6B",
                    padding: "8px 12px",
                    borderRadius: "4px",
                    fontSize: "13px",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    color: "#666",
                  }}
                >
                  <span>↔️</span> Drag the red bars to adjust column widths
                </div>
              </div>
              {configuringBox?.columnConfigs?.map((config, index) => (
                <div
                  key={index}
                  style={{
                    marginBottom: "10px",
                    padding: "10px",
                    background: "#f5f5f5",
                    borderRadius: "4px",
                  }}
                >
                  <div>Column {index + 1}</div>
                  <div>Width: {config.columnWidthPercentage?.toFixed(1)}%</div>
                </div>
              ))}

              <button
                onClick={() => setConfigStep("columnConfig")}
                style={{
                  width: "100%",
                  padding: "10px",
                  background: "#4CAF50",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  marginBottom: "10px",
                }}
              >
                Configure Column Types
              </button>
            </div>
          )}

          {configStep === "columnConfig" && (
            <div>
              <h4 style={{ margin: "0 0 10px 0" }}>Configure Column Types</h4>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                  marginBottom: "20px",
                }}
              >
                {configuringBox?.columnConfigs?.map((config, index) => (
                  <div
                    key={index}
                    style={{
                      padding: "10px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                    }}
                  >
                    <div style={{ marginBottom: "8px" }}>
                      <label>Column {index + 1} Name:</label>
                      <div>
                        Width: {config.columnWidthPercentage?.toFixed(1)}%
                      </div>
                      <input
                        type="text"
                        value={config.name}
                        onChange={(e) =>
                          handleColumnConfigChange(
                            index,
                            "name",
                            e.target.value
                          )
                        }
                        style={{
                          width: "100%",
                          padding: "4px",
                          marginTop: "4px",
                          borderRadius: "4px",
                          border: "1px solid #ccc",
                        }}
                      />
                    </div>
                    <div>
                      <label>Type:</label>
                      <select
                        value={config.type}
                        onChange={(e) => {
                          const value = e.target?.value;
                          if (!value) return;
                          setBoxes((prev) =>
                            prev.map((box) =>
                              box.id === selectedBoxId && box.columnConfigs
                                ? {
                                    ...box,
                                    columnConfigs: box.columnConfigs.map(
                                      (col, i) =>
                                        i === index
                                          ? {
                                              ...col,
                                              type: value as TextFieldType,
                                            }
                                          : col
                                    ),
                                  }
                                : box
                            )
                          );
                        }}
                        style={{
                          width: "100%",
                          padding: "4px",
                          marginTop: "4px",
                          borderRadius: "4px",
                          border: "1px solid #ccc",
                        }}
                      >
                        <option value="text">Text</option>
                        <option value="number">Number</option>
                        <option value="date">Date</option>
                      </select>
                    </div>
                  </div>
                ))}
              </div>
              <button
                onClick={() => setConfigStep("pageConfig")}
                style={{
                  width: "100%",
                  padding: "10px",
                  background: "#4CAF50",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  marginBottom: "10px",
                }}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

      {/* Rectangle Sidebar */}
      <div
        style={{
          position: "fixed",
          right: "20px",
          top: "100px",
          width: "300px",
          padding: "20px",
          background: "#f5f5f5",
          borderRadius: "8px",
          zIndex: 10,
          display: "flex",
          flexDirection: "column",
          gap: "20px",
          height: "calc(100% - 120px)",
          overflowY: "auto",
        }}
      >
        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          {/* <input
            type="text"
            value={template?.name || ""}
            onChange={(e) => {
              dispatch(
                templateSetData({
                  template: { ...template, name: e.target.value },
                })
              );
              setIsTemplateNameValid(true);
            }}
            placeholder="Template Name"
            style={{
              flex: 1,
              padding: "8px",
              borderRadius: "4px",
              border: isTemplateNameValid
                ? "1px solid #ccc"
                : "1px solid #ff4444",
              fontSize: "13px",
              backgroundColor: isTemplateNameValid ? "white" : "#fff8f8",
            }}
          />
          {!isTemplateNameValid && (
            <div
              style={{
                color: "#ff4444",
                fontSize: "12px",
                marginTop: "4px",
                position: "absolute",
                top: "100%",
                left: 0,
              }}
            >
              Template name already exists
            </div>
          )} */}
          <button
            onClick={() => handleSaveTemplate(true)}
            className="btn btn-success d-flex align-items-center gap-2"
          >
            <FontAwesomeIcon icon="save" />
            <span className="ml-1">
              {templateId ? "Update & Extract" : "Save & Extract"}
            </span>
          </button>
        </div>

        <div>
          <h3>Fields</h3>
          <div style={{ display: "flex", gap: "8px", marginBottom: "20px" }}>
            <select
              value={selectedBoxId || ""}
              onChange={(e) => handleBoxSelect(e.target.value)}
              style={{
                flex: 1,
                padding: "8px",
                borderRadius: "4px",
                border: "1px solid #ccc",
              }}
            >
              <option value="">Select a rectangle</option>
              {visibleBoxes.map((box) => (
                <option key={box.id} value={box.id}>
                  {box.name || `Field ${box.id.slice(-4)}`} -{" "}
                  {box.fieldType === "table"
                    ? "Table"
                    : box.textFieldType === "number"
                    ? "Number"
                    : box.textFieldType === "date"
                    ? "Date"
                    : "Text"}
                  {box.appliesToAllPages
                    ? " (All Pages)"
                    : ` (Page ${box.pageNumber})`}
                </option>
              ))}
            </select>
            <button
              onClick={handleDeleteBox}
              disabled={!selectedBoxId || configStep !== "complete"}
              style={{
                padding: "6px 12px",
                // background: "#FF6B6B",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                opacity: !selectedBoxId || configStep !== "complete" ? 0.5 : 1,
                display: "flex",
                alignItems: "center",
                gap: "4px",
                fontSize: "13px",
                whiteSpace: "nowrap",
              }}
            >
              <span>🗑️</span>
            </button>
          </div>
          {true && (
            <div style={{ marginBottom: "20px" }}>
              <strong>Extracted Data:</strong>
              {isLoadingData ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    padding: "20px",
                  }}
                >
                  <ClipLoader loading={true} size={20} color="#4CAF50" />
                </div>
              ) : (
                <pre
                  style={{
                    background: "white",
                    padding: "10px",
                    borderRadius: "4px",
                    fontSize: "12px",
                    overflow: "auto",
                    maxHeight: "200px",
                    marginTop: "8px",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {(() => {
                    try {
                      const parsed = JSON.parse(extractedData || "");
                      const text = parsed?.extractedText;
                      const isTable = ["table", "ocrtables"].includes(
                        selectedBox?.fieldType || ""
                      );

                      if (isTable) {
                        const rows = parseCsvToRowsAndColumn(text || "");
                        return (
                          <div>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "flex-end",
                                gap: "10px",
                                marginBottom: "10px",
                              }}
                            >
                              <CopyToClipboard
                                text={rows
                                  .map((row) => row.join(","))
                                  .join("\n")}
                              >
                                <Button variant="outline-secondary" size="sm">
                                  <FontAwesomeIcon
                                    icon="copy"
                                    className="mr-1"
                                  />
                                  Copy CSV
                                </Button>
                              </CopyToClipboard>
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => {
                                  const csvContent = rows
                                    .map((row) => row.join(","))
                                    .join("\n");
                                  const blob = new Blob([csvContent], {
                                    type: "text/csv",
                                  });
                                  const url = window.URL.createObjectURL(blob);
                                  const a = document.createElement("a");
                                  a.href = url;
                                  a.download = `${
                                    selectedBox?.name || "extracted_data"
                                  }.csv`;
                                  document.body.appendChild(a);
                                  a.click();
                                  window.URL.revokeObjectURL(url);
                                  document.body.removeChild(a);
                                }}
                              >
                                <FontAwesomeIcon
                                  icon="download"
                                  className="mr-1"
                                />
                                Download CSV
                              </Button>
                            </div>
                            <div
                              style={{ maxHeight: "400px", overflow: "auto" }}
                            >
                              <CsvToHtmlTable
                                rows={rows}
                                csvDelimiter=","
                                tableClassName="table table-striped table-hover"
                                tableRowClassName=""
                                tableColumnClassName=""
                                hasHeader={true}
                              />
                            </div>
                          </div>
                        );
                      } else {
                        return (
                          <div>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "flex-end",
                                gap: "10px",
                                marginBottom: "10px",
                              }}
                            >
                              <CopyToClipboard text={text || ""}>
                                <Button variant="outline-secondary" size="sm">
                                  <FontAwesomeIcon
                                    icon="copy"
                                    className="mr-1"
                                  />
                                  Copy
                                </Button>
                              </CopyToClipboard>
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => {
                                  const blob = new Blob([text || ""], {
                                    type: "text/plain",
                                  });
                                  const url = window.URL.createObjectURL(blob);
                                  const a = document.createElement("a");
                                  a.href = url;
                                  a.download = `${
                                    selectedBox?.name || "extracted_data"
                                  }.txt`;
                                  document.body.appendChild(a);
                                  a.click();
                                  window.URL.revokeObjectURL(url);
                                  document.body.removeChild(a);
                                }}
                              >
                                <FontAwesomeIcon
                                  icon="download"
                                  className="mr-1"
                                />
                                Download
                              </Button>
                            </div>
                            <pre
                              style={{
                                background: "white",
                                padding: "10px",
                                borderRadius: "4px",
                                fontSize: "12px",
                                overflow: "auto",
                                maxHeight: "200px",
                                marginTop: "8px",
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                              }}
                            >
                              {text ? text.trim() : extractedData}
                            </pre>
                          </div>
                        );
                      }
                    } catch {
                      return (
                        <div>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "flex-end",
                              gap: "10px",
                              marginBottom: "10px",
                            }}
                          >
                            <CopyToClipboard text={extractedData || ""}>
                              <Button variant="outline-secondary" size="sm">
                                <FontAwesomeIcon icon="copy" className="mr-1" />
                                Copy
                              </Button>
                            </CopyToClipboard>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => {
                                const blob = new Blob([extractedData || ""], {
                                  type: "text/plain",
                                });
                                const url = window.URL.createObjectURL(blob);
                                const a = document.createElement("a");
                                a.href = url;
                                a.download = `${
                                  selectedBox?.name || "extracted_data"
                                }.txt`;
                                document.body.appendChild(a);
                                a.click();
                                window.URL.revokeObjectURL(url);
                                document.body.removeChild(a);
                              }}
                            >
                              <FontAwesomeIcon
                                icon="download"
                                className="mr-1"
                              />
                              Download
                            </Button>
                          </div>
                          <pre
                            style={{
                              background: "white",
                              padding: "10px",
                              borderRadius: "4px",
                              fontSize: "12px",
                              overflow: "auto",
                              maxHeight: "200px",
                              marginTop: "8px",
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                            }}
                          >
                            {extractedData}
                          </pre>
                        </div>
                      );
                    }
                  })()}
                </pre>
              )}
            </div>
          )}
          {selectedBox && (
            <div
              style={{
                padding: "15px",
                background: "white",
                borderRadius: "4px",
                border: "1px solid #ddd",
              }}
            >
              <div style={{ marginBottom: "10px" }}>
                <strong>Name:</strong>
                <input
                  type="text"
                  value={selectedBox.name || ""}
                  onChange={handleNameChange}
                  style={{
                    width: "100%",
                    padding: "4px",
                    marginTop: "4px",
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                  }}
                />
              </div>
              {false && (
                <div style={{ marginBottom: "10px" }}>
                  <strong>Type:</strong>{" "}
                  {selectedBox?.fieldType === "table"
                    ? "Table"
                    : selectedBox?.textFieldType === "number"
                    ? "Number"
                    : selectedBox?.textFieldType === "date"
                    ? "Date"
                    : "Text"}
                </div>
              )}

              {selectedBox.fieldType === "text" && (
                <div style={{ marginBottom: "10px" }}>
                  <strong>Text Field Type:</strong>
                  <select
                    value={selectedBox.textFieldType || "text"}
                    onChange={(e) => {
                      const newType = e.target.value as TextFieldType;
                      setBoxes((prev) =>
                        prev.map((box) =>
                          box.id === selectedBoxId
                            ? { ...box, textFieldType: newType }
                            : box
                        )
                      );
                    }}
                    style={{
                      width: "100%",
                      padding: "4px",
                      marginTop: "4px",
                      borderRadius: "4px",
                      border: "1px solid #ccc",
                    }}
                  >
                    <option value="text">Text</option>
                    <option value="number">Number</option>
                    <option value="date">Date</option>
                  </select>
                </div>
              )}

              {selectedBox.fieldType === "table" && (
                <>
                  <div style={{ marginBottom: "10px" }}>
                    <strong>Number of Columns:</strong>
                    <input
                      type="number"
                      min="2"
                      max="10"
                      value={selectedBox.columns || 2}
                      onChange={(e) => {
                        const newCount = parseInt(e.target.value);
                        setBoxes((prev) =>
                          prev.map((box) =>
                            box.id === selectedBoxId
                              ? {
                                  ...box,
                                  columns: newCount,
                                  columnPositions: Array.from(
                                    { length: newCount - 1 },
                                    (_, i) => ((i + 1) * 100) / newCount
                                  ),
                                  columnConfigs: Array(newCount)
                                    .fill(null)
                                    .map((_, i) => ({
                                      name:
                                        box.columnConfigs?.[i]?.name ||
                                        `Column ${i + 1}`,
                                      type:
                                        box.columnConfigs?.[i]?.type || "text",
                                    })),
                                }
                              : box
                          )
                        );
                      }}
                      style={{
                        width: "100%",
                        padding: "4px",
                        marginTop: "4px",
                        borderRadius: "4px",
                        border: "1px solid #ccc",
                      }}
                    />
                  </div>

                  {selectedBox.columnConfigs &&
                    selectedBox.columnConfigs.length > 0 && (
                      <div style={{ marginBottom: "10px" }}>
                        <strong>Column Configuration:</strong>
                        <div
                          style={{
                            marginTop: "8px",
                            display: "flex",
                            flexDirection: "column",
                            gap: "8px",
                          }}
                        >
                          {selectedBox.columnConfigs.map((config, index) => (
                            <div
                              key={index}
                              style={{
                                padding: "8px",
                                border: "1px solid #ddd",
                                borderRadius: "4px",
                              }}
                            >
                              <div style={{ marginBottom: "8px" }}>
                                <label>Column {index + 1} Name:</label>
                                <input
                                  type="text"
                                  value={config.name}
                                  onChange={(e) => {
                                    const value = e.target?.value;
                                    if (value === undefined) return;
                                    setBoxes((prev) =>
                                      prev.map((box) =>
                                        box.id === selectedBoxId
                                          ? {
                                              ...box,
                                              columnConfigs:
                                                box.columnConfigs?.map(
                                                  (col, i) =>
                                                    i === index
                                                      ? {
                                                          ...col,
                                                          name: value,
                                                        }
                                                      : col
                                                ),
                                            }
                                          : box
                                      )
                                    );
                                  }}
                                  style={{
                                    width: "100%",
                                    padding: "4px",
                                    marginTop: "4px",
                                    borderRadius: "4px",
                                    border: "1px solid #ccc",
                                  }}
                                />
                              </div>
                              <div>
                                <label>Type:</label>
                                <select
                                  value={config.type}
                                  onChange={(e) => {
                                    const value = e.target?.value;
                                    if (!value) return;
                                    setBoxes((prev) =>
                                      prev.map((box) =>
                                        box.id === selectedBoxId &&
                                        box.columnConfigs
                                          ? {
                                              ...box,
                                              columnConfigs:
                                                box.columnConfigs.map(
                                                  (col, i) =>
                                                    i === index
                                                      ? {
                                                          ...col,
                                                          type: value as TextFieldType,
                                                        }
                                                      : col
                                                ),
                                            }
                                          : box
                                      )
                                    );
                                  }}
                                  style={{
                                    width: "100%",
                                    padding: "4px",
                                    marginTop: "4px",
                                    borderRadius: "4px",
                                    border: "1px solid #ccc",
                                  }}
                                >
                                  <option value="text">Text</option>
                                  <option value="number">Number</option>
                                  <option value="date">Date</option>
                                </select>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                </>
              )}
              {false && (
                <>
                  <div style={{ marginBottom: "10px" }}>
                    <strong>Position:</strong>
                    <div style={{ marginLeft: "10px" }}>
                      X: {Math.round(selectedBox?.x || 0)}px
                      <br />
                      Y: {Math.round(selectedBox?.y || 0)}px
                    </div>
                  </div>
                  <div style={{ marginBottom: "10px" }}>
                    <strong>Dimensions:</strong>
                    <div style={{ marginLeft: "10px" }}>
                      Width: {Math.round(selectedBox?.width || 0)}px
                      <br />
                      Height: {Math.round(selectedBox?.height || 0)}px
                    </div>
                  </div>
                  <div style={{ marginBottom: "10px" }}>
                    <strong>Area:</strong>{" "}
                    {Math.round(
                      (selectedBox?.width || 0) * (selectedBox?.height || 0)
                    )}
                    px²
                  </div>
                  <div style={{ marginBottom: "10px" }}>
                    <strong>Page:</strong>{" "}
                    {selectedBox?.appliesToAllPages
                      ? "All Pages"
                      : `Page ${selectedBox?.pageNumber}`}
                  </div>
                </>
              )}
              {/* <div style={{ marginBottom: "20px" }}>
                <button
                  onClick={() => {
                    const selectedBox = boxes.find(
                      (box) => box.id === selectedBoxId
                    );
                    fetchRectangleData(selectedBox);
                  }}
                  disabled={isLoadingData}
                  style={{
                    width: "100%",
                    padding: "8px",
                    background: isLoadingData ? "#ccc" : "#4CAF50",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: isLoadingData ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                >
                  {isLoadingData ? <>Loading...</> : <>🔍 Extract Data</>}
                </button>
              </div> */}

              <div style={{ marginBottom: "10px" }}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                  }}
                >
                  <label
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px",
                      padding: "12px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      cursor: "pointer",
                      background: !selectedBox.appliesToAllPages
                        ? "#f8f8f8"
                        : "white",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <input
                        type="radio"
                        checked={selectedBox?.appliesToAllPages === false}
                        onChange={() => {
                          setBoxes((prev) =>
                            prev.map((box) =>
                              box.id === selectedBoxId
                                ? {
                                    ...box,
                                    appliesToAllPages: false,
                                    pageNumber: currentPage,
                                  }
                                : box
                            )
                          );
                        }}
                      />
                      <span style={{ fontWeight: "500" }}>
                        Current Page Only
                      </span>
                    </div>
                    <div
                      style={{
                        marginLeft: "24px",
                        fontSize: "13px",
                        color: "#666",
                      }}
                    >
                      This field will only appear on page {currentPage}. Use
                      this for fields that are unique to this page.
                    </div>
                  </label>

                  <label
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px",
                      padding: "12px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      cursor: "pointer",
                      background: selectedBox.appliesToAllPages
                        ? "#f8f8f8"
                        : "white",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <input
                        type="radio"
                        checked={selectedBox?.appliesToAllPages === true}
                        onChange={() => {
                          setBoxes((prev) =>
                            prev.map((box) =>
                              box.id === selectedBoxId
                                ? {
                                    ...box,
                                    appliesToAllPages: true,
                                    pageNumber: undefined,
                                  }
                                : box
                            )
                          );
                        }}
                      />
                      <span style={{ fontWeight: "500" }}>All Pages</span>
                    </div>
                    <div
                      style={{
                        marginLeft: "24px",
                        fontSize: "13px",
                        color: "#666",
                      }}
                    >
                      This field will appear on every page of the document. Use
                      this for fields that repeat across pages.
                    </div>
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>

        {false && (
          <div>
            <h4>Debug Info</h4>
            <pre
              style={{
                background: "white",
                padding: "10px",
                borderRadius: "4px",
                fontSize: "12px",
                overflow: "auto",
              }}
            >
              {JSON.stringify(
                {
                  isDrawing,
                  startPos,
                  drawingBox: drawingBoxRef.current,
                  boxesCount: visibleBoxes.length,
                  configStep,
                  configuringBox,
                },
                null,
                2
              )}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
};

// Export the component directly
export const DrawRectangleV3 = DrawRectangleV3_;

// Export a simple connected component for testing
export const DrawRectangleV3Connected: React.FC = () => {
  return <DrawRectangleV3 />;
};

export default DrawRectangleV3Connected;
